import PropTypes from "prop-types";
import { useEffect, useRef } from "react";

// Default sections
import { FrontEndGrossProfit as FrontEndGrossProfitTier } from "@/PayPlans/helpers/DefaultSections";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { isProductDefined } from "../utils";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";
import { useReferenceState } from "@/Common/hooks/ReferenceState";

// Custom components
import FrontEndGrossProfit from "../FrontEndGrossProfit";
import MetricProducts from "@/PayPlans/components/Commons/MetricProducts";
import PVRvsPP from "../PVRvsPP";

// MUI
import { Grid } from "@mui/material";

const removeExtraProductsProps = (products = []) => {
	return products.map((product) => {
		// eslint-disable-next-line no-unused-vars, @typescript-eslint/no-unused-vars
		const { grossModifier, useDefaultRate, useGrossProfit, ...cleanProduct } = product;
		return cleanProduct;
	});
};

const getAreaIndex = (area = "tiers") => {
	const indexes = {
		tiers: 0,
		products: 1,
		frontEndGrossProfit: 2,
	};
	return indexes?.[area] || 0;
};

export default function CommissionModel(props) {
	const {
		model = {
			tiers: [],
			products: [],
			frontEndGrossProfit: [],
		},
		backProducts = [],
		backendProductsCount = 0,
		isSomeConfigured = true,
		loadingProducts = false,
		readOnly = false,
		onActionUpdate = () => {},
		onActionValidation = () => {},
	} = props;

	// Control to send update actions / References
	const wasUpdated = useRef(false);
	const [isValidAreas, setIsValidAreas, validAreas, recheckValidAreas] = useReferenceState(true, []);

	const onModelUpdate = (updatedModel) => {
		onActionValidation?.(isValidAreas);
		if (isValidAreas && wasUpdated.current) {
			onActionUpdate?.(updatedModel);
		}
	};
	const [innerModel, setInnerModel] = useCallbackState(null, onModelUpdate);

	const handleUpdate = (area, data) => {
		const updated = { ...innerModel, [area]: data };
		if (area === "products") {
			updated["products"] = removeExtraProductsProps(data);
		}
		wasUpdated.current = true;
		setInnerModel(updated);
	};

	const handleValidation = (area, value) => {
		const index = getAreaIndex(area);
		validAreas[index] = value;
		recheckValidAreas();
		onActionValidation?.(isValidAreas);
	};

	useEffect(() => {
		const defaultModel = {
			...model,
			frontEndGrossProfit: model?.frontEndGrossProfit?.length ? model.frontEndGrossProfit : [FrontEndGrossProfitTier()],
		};

		setInnerModel(clone(defaultModel));

		return () => {
			wasUpdated.current = false;
			setInnerModel(null);
			setIsValidAreas(true);
		};
	}, [model, setInnerModel, setIsValidAreas]);

	return (
		<Grid container>
			<Grid item xs={12}>
				<PVRvsPP
					title={"Total F&I Gross Profit"}
					tiers={innerModel?.tiers}
					isSomeConfigured={isSomeConfigured || isProductDefined(innerModel)}
					readOnly={readOnly}
					onActionUpdate={(tiers) => handleUpdate("tiers", tiers)}
					onActionValidation={(isValid) => handleValidation("tiers", isValid)}
					backendProductsCount={backendProductsCount}
				/>
			</Grid>
			<Grid item xs={12} mt={4}>
				<MetricProducts
					products={innerModel?.products || []}
					listProducts={backProducts}
					defaultTiers={{
						rows: innerModel?.tiers?.length || 1,
						columns: innerModel?.tiers?.[0]?.["commissions"]?.length || 1,
					}}
					loadingProducts={loadingProducts}
					hasPVRDefinition={true}
					hasTotalGross={true}
					readOnly={readOnly}
					onActionUpdate={(products) => handleUpdate("products", products)}
					onActionValidation={(isValid) => handleValidation("products", isValid)}
				/>
			</Grid>
			<Grid item xs={12}>
				<FrontEndGrossProfit
					title={"Total Front End Sales Gross Profit"}
					tiers={innerModel?.frontEndGrossProfit || []}
					readOnly={readOnly}
					onActionUpdate={(tiers) => handleUpdate("frontEndGrossProfit", tiers)}
					onActionValidation={(isValid) => handleValidation("frontEndGrossProfit", isValid)}
				/>
			</Grid>
		</Grid>
	);
}
CommissionModel.propTypes = {
	model: PropTypes.shape({
		tiers: PropTypes.array,
		products: PropTypes.array,
		frontEndGrossProfit: PropTypes.array,
	}),
	backendProductsCount: PropTypes.number,
	backProducts: PropTypes.array,
	isSomeConfigured: PropTypes.bool,
	loadingProducts: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
	onActionValidation: PropTypes.func,
};
